import React, { useState } from "react";
import { LoadMoreButton } from "components/common/LoadMoreButton";
import { StatusChip } from "components/common/StatusChip";
import { LogDays } from "@/components/main/governance/LogDays";
import type { ExportAuditEntry } from "@/lib/api/organizations";
import { Principal } from "@/components/main/governance/Principal";
import {
  exportFigures,
  exportPhrase,
  isPreview,
} from "@/components/main/governance/exportPhrasing";

interface Props {
  entries: ExportAuditEntry[];
  emptyLabel?: string;
}

export const ExportAuditList: React.FC<Props> = ({
  entries,
  emptyLabel = "No exports recorded.",
}) => {
  const [showPreviews, setShowPreviews] = useState(false);
  const previews = entries.filter(isPreview).length;
  const shown = showPreviews ? entries : entries.filter((e) => !isPreview(e));
  const fold =
    previews > 0 ? (
      <div className="px-2 pt-2">
        <LoadMoreButton
          onClick={() => setShowPreviews((v) => !v)}
          isLoading={false}
          label={
            showPreviews
              ? "Hide file previews"
              : `Show ${previews} file ${previews === 1 ? "preview" : "previews"}`
          }
        />
      </div>
    ) : null;

  if (shown.length === 0) {
    if (fold) return fold;
    return (
      <span className="px-2 text-fg-muted-regular-small">{emptyLabel}</span>
    );
  }

  return (
    <div className="flex flex-col">
      <LogDays
        entries={shown}
        at={(e) => e.created_at}
        line={(entry) => {
          const { verb, object, how, Mark } = exportPhrase(entry);
          return {
            key: entry.id,
            mark: <Mark sx={{ fontSize: 12 }} className="text-fg-muted" />,
            body: (
              <>
                <Principal
                  id={entry.actor_id}
                  name={entry.actor_name}
                  email={entry.actor_email}
                />{" "}
                {verb} <span className="text-fg-regular">{object}</span>
                {how && ` ${how}`}
                {` · ${exportFigures(entry)}`}
                {entry.outcome !== "completed" && (
                  <>
                    {" "}
                    <StatusChip tone="warning">{entry.outcome}</StatusChip>
                  </>
                )}
                {entry.space_name && (
                  <span className="text-fg-muted"> · {entry.space_name}</span>
                )}
              </>
            ),
          };
        }}
      />
      {fold}
    </div>
  );
};
